import {
  freqToGranularity,
  ValidDateTime,
  InvalidDateTime,
  DateAdapter,
  RuleOptionError,
  numberSortComparer,
  getDifferenceBetweenWeekdays,
  RecurrenceRuleError,
  uniqDateTimes,
  dateTimeSortComparer,
} from '@rschedule/core';

class RecurrenceRule {
  constructor(processor) {
    this.processor = processor;
    this.start = processor.start;
    this.end = processor.end;
    this.options = processor.options;
  }
}

class FrequencyRule extends RecurrenceRule {
  constructor(processor, initDate) {
    super(processor);
    this.initDate = initDate;
    this.intervalUnit = freqToGranularity(this.options.frequency);
    this.firstIntervalStartDate = this.normalizedStartDate(this.options.start);
    this.intervalStartDate = this.firstIntervalStartDate;
    this.intervalEndDate = this.normalizedEndDate(this.firstIntervalStartDate);
    this.skipToInterval(this.initDate);
  }
  run(date) {
    return this.validateDate(new ValidDateTime(date));
  }
  validateDate(arg) {
    const { date } = arg;
    if (arg instanceof ValidDateTime && this.dateIsWithinInterval(date)) {
      return arg;
    }
    this.skipToInterval(date);
    return new InvalidDateTime(
      // if the interval is 1, date will always be within the interval
      this.dateIsWithinInterval(date) ? date : this.setToCurrentInterval(),
    );
  }
  setToCurrentInterval() {
    return this.intervalStartDate;
  }
  normalizedStartDate(date) {
    if (this.options.frequency === 'WEEKLY') {
      return date.granularity('week', { weekStart: this.options.weekStart });
    }
    return date.granularity(this.intervalUnit);
  }
  normalizedEndDate(start) {
    switch (this.options.frequency) {
      case 'YEARLY':
        return start.add(1, 'year');
      case 'MONTHLY':
        return start.add(1, 'month');
      case 'WEEKLY':
        return start.add(1, 'week');
      case 'DAILY':
        return start.add(1, 'day');
      case 'HOURLY':
        return start.add(1, 'hour');
      case 'MINUTELY':
        return start.add(1, 'minute');
      case 'SECONDLY':
        return start.add(1, 'second');
      case 'MILLISECONDLY':
        return start.add(1, 'millisecond');
      default:
        throw new Error(`Unknown frequency ${this.options.frequency}`);
    }
  }
  skipToInterval(date) {
    const amount = this.intervalDifference(date);
    this.intervalStartDate = this.firstIntervalStartDate.add(amount, this.intervalUnit);
    this.intervalEndDate = this.normalizedEndDate(this.intervalStartDate);
  }
  dateIsWithinInterval(date) {
    return this.intervalStartDate.isBeforeOrEqual(date) && this.intervalEndDate.isAfter(date);
  }
  intervalDifference(date) {
    return intervalDifferenceBetweenDates({
      first: this.firstIntervalStartDate,
      second: date,
      unit: this.intervalUnit,
      interval: this.options.interval,
      weekStart: this.options.weekStart,
      direction: 'after',
    });
  }
}
/**
 * Given the frequency (unit) and interval, this function finds
 * how many jumps forward the first date needs in order to equal
 * or exceed the second date.
 *
 * For example:
 *
 * 1. Unit is daily and interval is 1. The second date is 3 days
 *    after the first. This will return 3.
 * 2. Unit is yearly and interval is 1. The second date is 3 days
 *    after the first. This will return 0.
 * 3. Unit is yearly and interval is 3. The second date is 4 years
 *    after the first. This will return 6.
 */
function intervalDifferenceBetweenDates({ first, second, unit, interval, weekStart, direction }) {
  let difference = (() => {
    let intervalDuration;
    let months;
    switch (unit) {
      case 'year':
        months = (second.get('year') - first.get('year')) * 12;
        months = months + second.get('month') - first.get('month');
        return Math.floor(months / 12);
      case 'month':
        months = (second.get('year') - first.get('year')) * 12;
        months = months + second.get('month') - first.get('month');
        return months;
      case 'week':
        first = first.granularity('week', { weekStart });
        intervalDuration = DateAdapter.MILLISECONDS_IN_WEEK;
        break;
      case 'day':
        intervalDuration = DateAdapter.MILLISECONDS_IN_DAY;
        break;
      case 'hour':
        intervalDuration = DateAdapter.MILLISECONDS_IN_HOUR;
        break;
      case 'minute':
        intervalDuration = DateAdapter.MILLISECONDS_IN_MINUTE;
        break;
      case 'second':
        intervalDuration = DateAdapter.MILLISECONDS_IN_SECOND;
        break;
      case 'millisecond':
        intervalDuration = 1;
        break;
      default:
        throw new Error('Unexpected `unit` value');
    }
    const diff = second.valueOf() - first.valueOf();
    return Math.floor(diff / intervalDuration);
  })();
  const fn = direction === 'after' ? Math.ceil : Math.floor;
  difference = fn(difference / interval) * interval;
  return difference;
}

class RevFrequencyRule extends FrequencyRule {
  setToCurrentInterval() {
    return this.intervalEndDate.subtract(1, 'millisecond');
  }
  intervalDifference(date) {
    return intervalDifferenceBetweenDates({
      first: this.firstIntervalStartDate,
      second: date,
      unit: this.intervalUnit,
      interval: this.options.interval,
      weekStart: this.options.weekStart,
      direction: 'before',
    });
  }
}

const FREQUENCIES = [
  'MILLISECONDLY',
  'SECONDLY',
  'MINUTELY',
  'HOURLY',
  'DAILY',
  'WEEKLY',
  'MONTHLY',
  'YEARLY',
];
const FrequencyRuleModule = {
  name: 'FrequencyRule',
  get: processor => {
    if (
      processor.options.byMillisecondOfSecond !== undefined ||
      processor.options.frequency !== 'MILLISECONDLY'
    ) {
      return null;
    }
    if (processor.reverse) return new RevFrequencyRule(processor, processor.end);
    return new FrequencyRule(processor, processor.start);
  },
  normalizeOptions: (options, norm) => {
    if (!FREQUENCIES.includes(options.frequency)) {
      throw new RuleOptionError(`"frequency" must be one of ${JSON.stringify(FREQUENCIES)}`);
    }
    if (options.interval !== undefined) {
      if (!Number.isInteger(options.interval)) {
        throw new RuleOptionError('"interval" expects a whole number');
      }
      if (options.interval < 1) {
        throw new RuleOptionError('"interval" cannot be less than 1');
      }
    }
    if (options.weekStart !== undefined) {
      if (!DateAdapter.WEEKDAYS.includes(options.weekStart)) {
        throw new RuleOptionError(
          `"weekStart" must be one of ${JSON.stringify(DateAdapter.WEEKDAYS)}`,
        );
      }
    }
    norm.frequency = options.frequency;
    norm.interval = options.interval || 1;
    norm.weekStart = options.weekStart || 'MO';
  },
  deps: () => [FrequencyRuleModule],
};

function ruleOptionFilled(option) {
  return Array.isArray(option) && option.length > 0;
}

const freqCache = new WeakMap();
class RecurrenceRuleBase extends RecurrenceRule {
  constructor(processor) {
    super(processor);
    if (!freqCache.has(this.processor)) {
      freqCache.set(this.processor, new FrequencyRule(processor, processor.start));
    }
    this.frequency = freqCache.get(this.processor);
  }
  validateDate(arg) {
    return this.frequency.validateDate(arg);
  }
}
class RevRecurrenceRuleBase extends RecurrenceRule {
  constructor(processor) {
    super(processor);
    if (!freqCache.has(this.processor)) {
      freqCache.set(this.processor, new RevFrequencyRule(processor, processor.end));
    }
    this.frequency = freqCache.get(this.processor);
  }
  validateDate(arg) {
    return this.frequency.validateDate(arg);
  }
}

/**
 * Contains shared logic for ByHourOfDay, ByMinuteOfHour,
 * BySecondOfMinute, and ByMillisecondOfSecond reverse rule pipes
 */
class RevByTimeOfUnitRule extends RevRecurrenceRuleBase {
  run(date) {
    // e.g. const currentTime = date.get('hour');
    const currentTime = date.get(this.granularity);
    // e.g. for (const time of this.options.byHourOfDay) {
    for (const time of this.option) {
      if (currentTime < time) continue;
      if (currentTime === time) {
        return this.validateDate(new ValidDateTime(date));
      }
      // e.g. return this.nextValidDate(args, date.endGranularity('day').set('hour', time));
      return this.validateDate(
        new InvalidDateTime(date.endGranularity(this.baseGranularity).set(this.granularity, time)),
      );
    }
    return this.validateDate(
      new InvalidDateTime(
        date
          // e.g. .endGranularity('day')
          .endGranularity(this.baseGranularity)
          // e.g. .subtract(1, 'day')
          .subtract(1, this.baseGranularity)
          // e.g. .set('hour', this.options.byHourOfDay[0]);
          .set(this.granularity, this.option[0]),
      ),
    );
  }
}

class RevByMillisecondOfSecondRule extends RevByTimeOfUnitRule {
  constructor() {
    super(...arguments);
    this.baseGranularity = 'second';
    this.granularity = 'millisecond';
    this.option = this.options.byMillisecondOfSecond.slice().reverse();
  }
}

/**
 * Contains shared logic for ByHourOfDay, ByMinuteOfHour,
 * BySecondOfMinute, and ByMillisecondOfSecond rule pipes
 */
class ByTimeOfUnitRule extends RecurrenceRuleBase {
  run(date) {
    // e.g. const currentTime = date.get('hour');
    const currentTime = date.get(this.granularity);
    // e.g. for (const time of this.options.byHourOfDay) {
    for (const time of this.option) {
      if (currentTime > time) continue;
      if (currentTime === time) {
        return this.validateDate(new ValidDateTime(date));
      }
      // e.g. return this.nextValidDate(args, date.granularity('day').set('hour', time));
      return this.validateDate(
        new InvalidDateTime(date.granularity(this.baseGranularity).set(this.granularity, time)),
      );
    }
    return this.validateDate(
      new InvalidDateTime(
        date
          // e.g. .granularity('day')
          .granularity(this.baseGranularity)
          // e.g. .add(1, 'day')
          .add(1, this.baseGranularity)
          // e.g. .set('hour', this.options.byHourOfDay[0]);
          .set(this.granularity, this.option[0]),
      ),
    );
  }
}

class ByMillisecondOfSecondRule extends ByTimeOfUnitRule {
  constructor() {
    super(...arguments);
    this.baseGranularity = 'second';
    this.granularity = 'millisecond';
    this.option = this.options.byMillisecondOfSecond;
  }
}

const ByMillisecondOfSecondRuleModule = {
  name: 'ByMillisecondOfSecond',
  get: processor => {
    if (processor.options.byMillisecondOfSecond === undefined) return null;
    if (processor.reverse) return new RevByMillisecondOfSecondRule(processor);
    return new ByMillisecondOfSecondRule(processor);
  },
  normalizeOptions: (options, norm) => {
    if (options.byMillisecondOfSecond !== undefined) {
      if (!ruleOptionFilled(options.byMillisecondOfSecond)) {
        throw new RuleOptionError('"byMillisecondOfSecond" expects a non-empty array');
      }
      if (options.byMillisecondOfSecond.some(num => num < 0 || num > 999)) {
        throw new RuleOptionError('"byMillisecondOfSecond" values must be >= 0 && <= 999');
      }
      norm.byMillisecondOfSecond = options.byMillisecondOfSecond;
      norm.byMillisecondOfSecond.sort(numberSortComparer);
    } else if (options.frequency !== 'MILLISECONDLY') {
      norm.byMillisecondOfSecond = [norm.start.get('millisecond')];
    }
  },
  deps: () => [FrequencyRuleModule, ByMillisecondOfSecondRuleModule],
};

class RevBySecondOfMinuteRule extends RevByTimeOfUnitRule {
  constructor() {
    super(...arguments);
    this.baseGranularity = 'minute';
    this.granularity = 'second';
    this.option = this.options.bySecondOfMinute.slice().reverse();
  }
}

class BySecondOfMinuteRule extends ByTimeOfUnitRule {
  constructor() {
    super(...arguments);
    this.baseGranularity = 'minute';
    this.granularity = 'second';
    this.option = this.options.bySecondOfMinute;
  }
}

const BySecondOfMinuteRuleModule = {
  name: 'BySecondOfMinute',
  get: processor => {
    if (processor.options.bySecondOfMinute === undefined) return null;
    if (processor.reverse) return new RevBySecondOfMinuteRule(processor);
    return new BySecondOfMinuteRule(processor);
  },
  normalizeOptions: (options, norm) => {
    if (options.bySecondOfMinute !== undefined) {
      if (!ruleOptionFilled(options.bySecondOfMinute)) {
        throw new RuleOptionError('"bySecondOfMinute" expects a non-empty array');
      }
      if (options.bySecondOfMinute.some(num => num < 0 || num > 60)) {
        throw new RuleOptionError('"bySecondOfMinute" values must be >= 0 && <= 60');
      }
      norm.bySecondOfMinute = options.bySecondOfMinute;
      norm.bySecondOfMinute.sort(numberSortComparer);
    } else if (!['SECONDLY', 'MILLISECONDLY'].includes(options.frequency)) {
      norm.bySecondOfMinute = [norm.start.get('second')];
    }
  },
  deps: () => [FrequencyRuleModule, BySecondOfMinuteRuleModule, ByMillisecondOfSecondRuleModule],
};

class RevByMinuteOfHourRule extends RevByTimeOfUnitRule {
  constructor() {
    super(...arguments);
    this.baseGranularity = 'hour';
    this.granularity = 'minute';
    this.option = this.options.byMinuteOfHour.slice().reverse();
  }
}

class ByMinuteOfHourRule extends ByTimeOfUnitRule {
  constructor() {
    super(...arguments);
    this.baseGranularity = 'hour';
    this.granularity = 'minute';
    this.option = this.options.byMinuteOfHour;
  }
}

const ByMinuteOfHourRuleModule = {
  name: 'ByMinuteOfHour',
  get: processor => {
    if (processor.options.byMinuteOfHour === undefined) return null;
    if (processor.reverse) return new RevByMinuteOfHourRule(processor);
    return new ByMinuteOfHourRule(processor);
  },
  normalizeOptions: (options, norm) => {
    if (options.byMinuteOfHour !== undefined) {
      if (!ruleOptionFilled(options.byMinuteOfHour)) {
        throw new RuleOptionError('"byMinuteOfHour" expects a non-empty array');
      }
      if (options.byMinuteOfHour.some(num => num < 0 || num > 59)) {
        throw new RuleOptionError('"byMinuteOfHour" values must be >= 0 && <= 59');
      }
      norm.byMinuteOfHour = options.byMinuteOfHour;
      norm.byMinuteOfHour.sort(numberSortComparer);
    } else if (!['MINUTELY', 'SECONDLY', 'MILLISECONDLY'].includes(options.frequency)) {
      norm.byMinuteOfHour = [norm.start.get('minute')];
    }
  },
  deps: () => [
    FrequencyRuleModule,
    ByMinuteOfHourRuleModule,
    BySecondOfMinuteRuleModule,
    ByMillisecondOfSecondRuleModule,
  ],
};

class RevByHourOfDayRule extends RevByTimeOfUnitRule {
  constructor() {
    super(...arguments);
    this.baseGranularity = 'day';
    this.granularity = 'hour';
    this.option = this.options.byHourOfDay.slice().reverse();
  }
}

class ByHourOfDayRule extends ByTimeOfUnitRule {
  constructor() {
    super(...arguments);
    this.baseGranularity = 'day';
    this.granularity = 'hour';
    this.option = this.options.byHourOfDay;
  }
}

const ByHourOfDayRuleModule = {
  name: 'ByHourOfDay',
  get: processor => {
    if (processor.options.byHourOfDay === undefined) return null;
    if (processor.reverse) return new RevByHourOfDayRule(processor);
    return new ByHourOfDayRule(processor);
  },
  normalizeOptions: (options, norm) => {
    if (options.byHourOfDay !== undefined) {
      if (!ruleOptionFilled(options.byHourOfDay)) {
        throw new RuleOptionError('"byHourOfDay" expects a non-empty array');
      }
      if (options.byHourOfDay.some(num => num < 0 || num > 23)) {
        throw new RuleOptionError('"byHourOfDay" values must be >= 0 && <= 23');
      }
      norm.byHourOfDay = options.byHourOfDay;
      norm.byHourOfDay.sort(numberSortComparer);
    } else if (['YEARLY', 'MONTHLY', 'WEEKLY', 'DAILY'].includes(options.frequency)) {
      norm.byHourOfDay = [norm.start.get('hour')];
    }
  },
  deps: () => [
    FrequencyRuleModule,
    ByHourOfDayRuleModule,
    ByMinuteOfHourRuleModule,
    BySecondOfMinuteRuleModule,
    ByMillisecondOfSecondRuleModule,
  ],
};

function getNextWeekday(date, weekday) {
  return date.add(getDifferenceBetweenWeekdays(date.get('weekday'), weekday), 'day');
}

function getNthWeekdayOfMonth(date, weekday, nth) {
  let base = date.set('day', 1);
  if (nth < 0) {
    base = base.add(1, 'month');
  }
  base = getNextWeekday(base, weekday);
  // when nth is negative, adding it will act as subtraction
  return nth < 0 ? base.add(nth, 'week') : base.add(nth - 1, 'week');
}

class ByDayOfMonthRule extends RecurrenceRuleBase {
  run(date) {
    const normalizedByDayOfMonth = normalizeByDayOfMonth(
      date,
      this.options.byDayOfMonth,
      this.options.byDayOfWeek,
    );
    const currentDay = date.get('day');
    for (const day of normalizedByDayOfMonth) {
      if (currentDay > day) continue;
      if (currentDay === day) {
        return this.validateDate(new ValidDateTime(date));
      }
      return this.validateDate(new InvalidDateTime(date.granularity('month').set('day', day)));
    }
    let next;
    let nextMonth = date;
    let index = 0;
    while (!next && index < 30) {
      nextMonth = nextMonth.granularity('month').add(1, 'month');
      next = normalizeByDayOfMonth(
        nextMonth,
        this.options.byDayOfMonth,
        this.options.byDayOfWeek,
      )[0];
      index++;
    }
    if (index >= 13) {
      throw new RecurrenceRuleError('byDayOfMonth Infinite while loop');
    }
    return this.validateDate(new InvalidDateTime(nextMonth.set('day', next)));
  }
}
/**
 * Does a few things:
 *
 * 1. filters out byDayOfMonth entries which are not applicable
 *    to current month
 * 2. negative entries to positive ones
 * 3. if a byDayOfWeek option is given, removes days which are
 *    not on the correct day of the week
 */
function normalizeByDayOfMonth(date, byDayOfMonth, byDayOfWeek) {
  const lengthOfMonth = date.endGranularity('month').get('day');
  let normalizedByDayOfMonth = byDayOfMonth
    .filter(day => lengthOfMonth >= Math.abs(day))
    .map(day => (day > 0 ? day : lengthOfMonth + day + 1));
  if (byDayOfWeek) {
    const base = date.granularity('month');
    const filteredByDayOfMonth = [];
    byDayOfWeek.forEach(entry => {
      if (typeof entry === 'string') {
        filteredByDayOfMonth.push(
          ...normalizedByDayOfMonth.filter(day => base.set('day', day).get('weekday') === entry),
        );
        return;
      }
      const nthWeekdayOfMonth = getNthWeekdayOfMonth(date, ...entry).get('day');
      if (normalizedByDayOfMonth.includes(nthWeekdayOfMonth)) {
        filteredByDayOfMonth.push(nthWeekdayOfMonth);
      }
    });
    normalizedByDayOfMonth = Array.from(new Set(filteredByDayOfMonth));
  }
  return normalizedByDayOfMonth.sort((a, b) => {
    if (a > b) return 1;
    if (a < b) return -1;
    else return 0;
  });
}

class RevByDayOfMonthRule extends RevRecurrenceRuleBase {
  run(date) {
    const normalizedByDayOfMonth = normalizeByDayOfMonth(
      date,
      this.options.byDayOfMonth,
      this.options.byDayOfWeek,
    ).reverse();
    const currentDay = date.get('day');
    for (const day of normalizedByDayOfMonth) {
      if (currentDay < day) continue;
      if (currentDay === day) {
        return this.validateDate(new ValidDateTime(date));
      }
      return this.validateDate(new InvalidDateTime(date.endGranularity('month').set('day', day)));
    }
    let next;
    let nextMonth = date;
    let index = 0;
    while (!next && index < 30) {
      nextMonth = nextMonth.endGranularity('month').subtract(1, 'month');
      next = normalizeByDayOfMonth(
        nextMonth,
        this.options.byDayOfMonth,
        this.options.byDayOfWeek,
      ).pop();
      index++;
    }
    if (index >= 13) {
      throw new RecurrenceRuleError('byDayOfMonth Infinite while loop');
    }
    return this.validateDate(new InvalidDateTime(nextMonth.set('day', next)));
  }
}

const ByDayOfMonthRuleModule = {
  name: 'ByDayOfMonth',
  get: processor => {
    if (processor.options.byDayOfMonth === undefined) return null;
    if (processor.reverse) return new RevByDayOfMonthRule(processor);
    return new ByDayOfMonthRule(processor);
  },
  normalizeOptions: (options, norm) => {
    if (options.byDayOfMonth !== undefined) {
      if (options.frequency === 'WEEKLY') {
        throw new RuleOptionError('when "frequency" is "WEEKLY", "byDayOfMonth" cannot be present');
      }
      if (!ruleOptionFilled(options.byDayOfMonth)) {
        throw new RuleOptionError('"byDayOfMonth" expects a non-empty array');
      }
      if (options.byDayOfMonth.some(num => num === 0 || num < -31 || num > 31)) {
        throw new RuleOptionError(
          '"byDayOfMonth" values must be `num !== 0 && num <= 31 && num >= -31`',
        );
      }
      norm.byDayOfMonth = options.byDayOfMonth.slice();
    } else if (
      !(ruleOptionFilled(options.byDayOfWeek) || ruleOptionFilled(options.byDayOfYear)) &&
      ['YEARLY', 'MONTHLY'].includes(options.frequency)
    ) {
      norm.byDayOfMonth = [norm.start.get('day')];
    }
  },
  deps: () => [
    FrequencyRuleModule,
    ByDayOfMonthRuleModule,
    ByHourOfDayRuleModule,
    ByMinuteOfHourRuleModule,
    BySecondOfMinuteRuleModule,
    ByMillisecondOfSecondRuleModule,
  ],
};

function getNthWeekdayOfYear(date, weekday, nth) {
  let base = date.set('month', 1).set('day', 1);
  if (nth < 0) {
    base = base.add(1, 'year');
  }
  base = getNextWeekday(base, weekday);
  // when nth is negative, adding it will act as subtraction
  return nth < 0 ? base.add(nth, 'week') : base.add(nth - 1, 'week');
}

function getPreviousWeekday(date, weekday) {
  const diff = getDifferenceBetweenWeekdays(date.get('weekday'), weekday);
  return date.subtract(diff === 0 ? 0 : 7 - diff, 'day');
}

class RevByDayOfWeekRule extends RevRecurrenceRuleBase {
  run(date) {
    if (this.options.frequency === 'YEARLY') {
      return this.options.byMonthOfYear === undefined
        ? this.expandYearly(date)
        : this.expandMonthly(date);
    } else if (this.options.frequency === 'MONTHLY') {
      return this.expandMonthly(date);
    }
    return this.expand(date);
  }
  expandYearly(date) {
    let next = getPrevWeekdaysOfYear(date, this.options.byDayOfWeek)[0];
    let index = 0;
    let base = date;
    // If we can't find a valid date this year,
    // search the previous year. Only search the past 28 years.
    // (the calendar repeats on a 28 year cycle, according
    // to the internet).
    while (!next && index < 28) {
      index++;
      base = base.granularity('year').subtract(1, 'millisecond');
      next = getPrevWeekdaysOfYear(base, this.options.byDayOfWeek)[0];
    }
    if (!next) {
      throw new RecurrenceRuleError(
        'The byDayOfWeek rule appears to contain an impossible combination',
      );
    }
    return this.result(date, next);
  }
  expandMonthly(date) {
    let next = getPrevWeekdaysOfMonth(date, this.options.byDayOfWeek)[0];
    let index = 0;
    let base = date;
    // TODO: performance improvment
    // If, in the first year, a match isn't found, we should be able to
    // jumpt to the previous leap year and check that. Or, if already on
    // a leap year, we can just error immediately.
    // If we can't find a valid date this month,
    // search the previous month. Only search the past 4 years
    // (to account for leap year).
    while (!next && index < 50) {
      index++;
      base = base.granularity('month').subtract(1, 'millisecond');
      next = getPrevWeekdaysOfMonth(base, this.options.byDayOfWeek)[0];
    }
    if (!next) {
      throw new RecurrenceRuleError(
        'The byDayOfWeek rule appears to contain an impossible combination',
      );
    }
    return this.result(date, next);
  }
  expand(date) {
    const next = this.options.byDayOfWeek
      .map(weekday => getPreviousWeekday(date, weekday))
      .sort(dateTimeSortComparer)
      .pop();
    return this.result(date, next);
  }
  result(date, next) {
    if (next.isEqual(date)) {
      return this.validateDate(new ValidDateTime(date));
    }
    return this.validateDate(new InvalidDateTime(next.endGranularity('day')));
  }
}
/** For each byDayOfWeek entry, find the previous DateTime */
function getPrevWeekdaysOfYear(date, byDayOfWeek) {
  const normalizedNthWeekdaysOfYear = byDayOfWeek
    .filter(entry => Array.isArray(entry))
    .map(entry => getNthWeekdayOfYear(date, ...entry));
  const normalizedNextWeekdays = byDayOfWeek
    .filter(entry => typeof entry === 'string')
    .map(weekday => getPreviousWeekday(date, weekday))
    .filter(entry => entry.get('year') === date.get('year'));
  return uniqDateTimes([...normalizedNthWeekdaysOfYear, ...normalizedNextWeekdays])
    .filter(entry => entry.isBeforeOrEqual(date))
    .sort(dateTimeSortComparer)
    .reverse();
}
/** For each byDayOfWeek entry, find the previous DateTime */
function getPrevWeekdaysOfMonth(date, byDayOfWeek) {
  const normalizedNthWeekdaysOfMonth = byDayOfWeek
    .filter(entry => Array.isArray(entry))
    .map(entry => getNthWeekdayOfMonth(date, ...entry));
  const normalizedNextWeekdays = byDayOfWeek
    .filter(entry => typeof entry === 'string')
    .map(weekday => getPreviousWeekday(date, weekday))
    .filter(
      entry => entry.get('year') === date.get('year') && entry.get('month') === date.get('month'),
    );
  return uniqDateTimes([...normalizedNthWeekdaysOfMonth, ...normalizedNextWeekdays])
    .filter(entry => entry.isBeforeOrEqual(date))
    .sort(dateTimeSortComparer)
    .reverse();
}

class ByDayOfWeekRule extends RecurrenceRuleBase {
  run(date) {
    if (this.options.frequency === 'YEARLY') {
      return this.options.byMonthOfYear === undefined
        ? this.expandYearly(date)
        : this.expandMonthly(date);
    } else if (this.options.frequency === 'MONTHLY') {
      return this.expandMonthly(date);
    }
    return this.expand(date);
  }
  expandYearly(date) {
    let next = getNextWeekdaysOfYear(date, this.options.byDayOfWeek)[0];
    let index = 0;
    let base = date;
    // If we can't find a valid date this year,
    // search next year. Only search the next 28 years.
    // (the calendar repeats on a 28 year cycle, according
    // to the internet).
    while (!next && index < 28) {
      index++;
      base = base.granularity('year').add(1, 'year');
      next = getNextWeekdaysOfYear(base, this.options.byDayOfWeek)[0];
    }
    if (!next) {
      throw new RecurrenceRuleError(
        'The byDayOfWeek rule appears to contain an impossible combination',
      );
    }
    return this.result(date, next);
  }
  expandMonthly(date) {
    let next = getNextWeekdaysOfMonth(date, this.options.byDayOfWeek)[0];
    let index = 0;
    let base = date;
    // TODO: performance improvment
    // If, in the first year, a match isn't found, we should be able to
    // jumpt to the next leap year and check that. Or, if already on
    // a leap year, we can just error immediately.
    // If we can't find a valid date this month,
    // search the next month. Only search the next 4 years
    // (to account for leap year).
    while (!next && index < 50) {
      index++;
      base = base.granularity('month').add(1, 'month');
      next = getNextWeekdaysOfMonth(base, this.options.byDayOfWeek)[0];
    }
    if (!next) {
      throw new RecurrenceRuleError(
        'The byDayOfWeek rule appears to contain an impossible combination',
      );
    }
    return this.result(date, next);
  }
  expand(date) {
    const next = this.options.byDayOfWeek
      .map(weekday => getNextWeekday(date, weekday))
      .sort(dateTimeSortComparer)[0];
    return this.result(date, next);
  }
  result(date, next) {
    if (next.isEqual(date)) {
      return this.validateDate(new ValidDateTime(date));
    }
    return this.validateDate(new InvalidDateTime(next.granularity('day')));
  }
}
/** For each byDayOfWeek entry, find the next DateTime */
function getNextWeekdaysOfYear(date, byDayOfWeek) {
  const normalizedNthWeekdaysOfYear = byDayOfWeek
    .filter(entry => Array.isArray(entry))
    .map(entry => getNthWeekdayOfYear(date, ...entry));
  const normalizedNextWeekdays = byDayOfWeek
    .filter(entry => typeof entry === 'string')
    .map(weekday => getNextWeekday(date, weekday))
    .filter(entry => entry.get('year') === date.get('year'));
  return uniqDateTimes([...normalizedNthWeekdaysOfYear, ...normalizedNextWeekdays])
    .filter(entry => entry.isAfterOrEqual(date))
    .sort(dateTimeSortComparer);
}
/** For each byDayOfWeek entry, find the next DateTime */
function getNextWeekdaysOfMonth(date, byDayOfWeek) {
  const normalizedNthWeekdaysOfMonth = byDayOfWeek
    .filter(entry => Array.isArray(entry))
    .map(entry => getNthWeekdayOfMonth(date, ...entry));
  const normalizedNextWeekdays = byDayOfWeek
    .filter(entry => typeof entry === 'string')
    .map(weekday => getNextWeekday(date, weekday))
    .filter(
      entry => entry.get('year') === date.get('year') && entry.get('month') === date.get('month'),
    );
  return uniqDateTimes([...normalizedNthWeekdaysOfMonth, ...normalizedNextWeekdays])
    .filter(entry => entry.isAfterOrEqual(date))
    .sort(dateTimeSortComparer);
}

const ByDayOfWeekRuleModule = {
  name: 'ByDayOfWeek',
  get: processor => {
    if (processor.options.byDayOfWeek === undefined) return null;
    if (processor.reverse) return new RevByDayOfWeekRule(processor);
    return new ByDayOfWeekRule(processor);
  },
  normalizeOptions: (options, norm) => {
    if (options.byDayOfWeek !== undefined) {
      if (!ruleOptionFilled(options.byDayOfWeek)) {
        throw new RuleOptionError('"byDayOfWeek" expects a non-empty array');
      }
      const invalidWeeday = options.byDayOfWeek.find(day =>
        Array.isArray(day)
          ? !DateAdapter.WEEKDAYS.includes(day[0])
          : !DateAdapter.WEEKDAYS.includes(day),
      );
      if (invalidWeeday) {
        throw new RuleOptionError(
          `"byDayOfWeek" expects weedays in the form ` +
            `${JSON.stringify(DateAdapter.WEEKDAYS)} but "${invalidWeeday}" was provided`,
        );
      }
      if (
        !['YEARLY', 'MONTHLY'].includes(options.frequency) &&
        options.byDayOfWeek.some(weekday => Array.isArray(weekday))
      ) {
        throw new RuleOptionError(
          '"byDayOfWeek" can only include a numeric value (i.e. `[string, number]`) when the "frequency" is ' +
            'either "MONTHLY" or "YEARLY"',
        );
      }
      if (
        options.frequency === 'MONTHLY' &&
        options.byDayOfWeek.some(
          weekday =>
            Array.isArray(weekday) && (weekday[1] < -31 || weekday[1] === 0 || weekday[1] > 31),
        )
      ) {
        throw new RuleOptionError(
          'when "frequency" is "MONTHLY", each "byDayOfWeek" can optionally only' +
            ' have a numeric value >= -31 and <= 31 and !== 0',
        );
      }
      if (
        options.frequency === 'YEARLY' &&
        options.byDayOfWeek.some(
          weekday =>
            Array.isArray(weekday) && (weekday[1] < -366 || weekday[1] === 0 || weekday[1] > 366),
        )
      ) {
        throw new RuleOptionError(
          'when "frequency" is "YEARLY", each "byDayOfWeek" can optionally only' +
            ' have a numeric value >= -366 and <= 366 and !== 0',
        );
      }
      norm.byDayOfWeek = options.byDayOfWeek;
    } else if (!ruleOptionFilled(options.byDayOfMonth) && options.frequency === 'WEEKLY') {
      norm.byDayOfWeek = [norm.start.get('weekday')];
    }
  },
  deps: () => [
    FrequencyRuleModule,
    ByDayOfWeekRuleModule,
    ByHourOfDayRuleModule,
    ByMinuteOfHourRuleModule,
    BySecondOfMinuteRuleModule,
    ByMillisecondOfSecondRuleModule,
  ],
};

class RevByMonthOfYearRule extends RevRecurrenceRuleBase {
  constructor() {
    super(...arguments);
    this.option = this.processor.options.byMonthOfYear.slice().reverse();
  }
  run(date) {
    const currentMonth = date.get('month');
    for (const month of this.option) {
      if (currentMonth < month) continue;
      if (currentMonth === month) {
        return this.validateDate(new ValidDateTime(date));
      }
      return this.validateDate(
        new InvalidDateTime(date.endGranularity('year').set('month', month)),
      );
    }
    return this.validateDate(
      new InvalidDateTime(
        date
          .endGranularity('year')
          .subtract(1, 'year')
          .set('month', this.option[0]),
      ),
    );
  }
}

class ByMonthOfYearRule extends RecurrenceRuleBase {
  run(date) {
    const currentMonth = date.get('month');
    for (const month of this.options.byMonthOfYear) {
      if (currentMonth > month) continue;
      if (currentMonth === month) {
        return this.validateDate(new ValidDateTime(date));
      }
      return this.validateDate(new InvalidDateTime(date.granularity('year').set('month', month)));
    }
    return this.validateDate(
      new InvalidDateTime(
        date
          .granularity('year')
          .add(1, 'year')
          .set('month', this.options.byMonthOfYear[0]),
      ),
    );
  }
}

const ByMonthOfYearRuleModule = {
  name: 'ByMonthOfYear',
  get: processor => {
    if (processor.options.byMonthOfYear === undefined) return null;
    if (processor.reverse) {
      return new RevByMonthOfYearRule(processor);
    }
    return new ByMonthOfYearRule(processor);
  },
  normalizeOptions: (options, norm) => {
    if (options.byMonthOfYear !== undefined) {
      if (!ruleOptionFilled(options.byMonthOfYear)) {
        throw new RuleOptionError('"byMonthOfYear" expects a non-empty array');
      }
      if (options.byMonthOfYear.some(num => num < 1 || num > 12)) {
        throw new RuleOptionError('"byMonthOfYear" values must be `num >= 1 && num >= 12`');
      }
      norm.byMonthOfYear = options.byMonthOfYear.slice();
      norm.byMonthOfYear.sort(numberSortComparer);
    } else if (
      !(
        ruleOptionFilled(options.byDayOfMonth) ||
        ruleOptionFilled(options.byDayOfWeek) ||
        ruleOptionFilled(options.byDayOfYear)
      ) &&
      options.frequency === 'YEARLY'
    ) {
      norm.byMonthOfYear = [norm.start.get('month')];
    }
  },
  deps: () => [
    FrequencyRuleModule,
    ByMonthOfYearRuleModule,
    ByDayOfMonthRuleModule,
    ByHourOfDayRuleModule,
    ByMinuteOfHourRuleModule,
    BySecondOfMinuteRuleModule,
    ByMillisecondOfSecondRuleModule,
  ],
};

const ICAL_RULES = [
  FrequencyRuleModule,
  ByMonthOfYearRuleModule,
  ByDayOfMonthRuleModule,
  ByDayOfWeekRuleModule,
  ByHourOfDayRuleModule,
  ByMinuteOfHourRuleModule,
  BySecondOfMinuteRuleModule,
  ByMillisecondOfSecondRuleModule,
];

export {
  ByDayOfMonthRule,
  ByDayOfMonthRuleModule,
  ByDayOfWeekRule,
  ByDayOfWeekRuleModule,
  ByHourOfDayRule,
  ByHourOfDayRuleModule,
  ByMillisecondOfSecondRule,
  ByMillisecondOfSecondRuleModule,
  ByMinuteOfHourRule,
  ByMinuteOfHourRuleModule,
  ByMonthOfYearRule,
  ByMonthOfYearRuleModule,
  BySecondOfMinuteRule,
  BySecondOfMinuteRuleModule,
  FrequencyRule,
  FrequencyRuleModule,
  ICAL_RULES,
  RevByDayOfMonthRule,
  RevByDayOfWeekRule,
  RevByHourOfDayRule,
  RevByMillisecondOfSecondRule,
  RevByMinuteOfHourRule,
  RevByMonthOfYearRule,
  RevBySecondOfMinuteRule,
  RevFrequencyRule,
  getPrevWeekdaysOfMonth,
  getPrevWeekdaysOfYear,
};
